import type { ActionGraph } from "../actions.ts";
import { ACTION_SPECS } from "../schemaSpec.ts";
import type { Item, ProjectView } from "../entities.ts";
import { issue } from "./helpers.ts";
import type { ValidationReport } from "./types.ts";

const ENTITY_ONLY_ENTRY_EVENTS = new Set(["OnSpawn", "OnTouch"]);

const PLAYER_BRINGING_NODE_TYPES = new Set(["Interact", "OnConsume"]);

const PLAYER_BRINGING_HANDLES: Record<string, ReadonlySet<string>> = {
	OnProximity: new Set(["OnEnter", "OnExit"]),
};

const PLAYER_REQUIRED_NODE_TYPES = new Set(
	ACTION_SPECS
		.filter((s) => s.contextRequiresPlayer && !s.skippedWhenMissingContext)
		.map((s) => s.type as string),
);

const PLAYER_REQUIRED_CONDITION_TYPES = new Set(["HasItem", "HasStat", "HasCurrency"]);

const branchRequiresPlayer = (node: ActionGraph["nodes"][string]): boolean => {
	if (node.type !== "Branch") return false;
	if (!Array.isArray(node.conditions)) return false;
	return node.conditions.some((condition) => {
		if (typeof condition !== "object" || condition === null) return true;
		const conditionType = (condition as { type?: unknown }).type;
		return typeof conditionType !== "string" || PLAYER_REQUIRED_CONDITION_TYPES.has(conditionType);
	});
};

export const validatePlayerRequiredReachability = (
	graph: ActionGraph,
	item: Item,
	_view: ProjectView,
	entityId: string,
	report: ValidationReport,
): void => {

	if (item.category === "Player") return;

	type EdgeOut = { handle: string; target: string };
	const adjacency = new Map<string, EdgeOut[]>();
	for (const edge of graph.edges) {
		if (!graph.nodes[edge.source] || !graph.nodes[edge.target]) continue;
		const list = adjacency.get(edge.source) ?? [];
		list.push({ handle: edge.sourceHandle, target: edge.target });
		adjacency.set(edge.source, list);
	}

	const reported = new Set<string>();
	for (const [entryId, entryNode] of Object.entries(graph.nodes)) {
		if (!ENTITY_ONLY_ENTRY_EVENTS.has(entryNode.type)) continue;

		const stack: Array<[string, boolean]> = [[entryId, false]];
		const visited = new Set<string>();
		while (stack.length > 0) {
			const [nodeId, hasPlayer] = stack.pop() as [string, boolean];
			const stateKey = `${nodeId}:${hasPlayer ? 1 : 0}`;
			if (visited.has(stateKey)) continue;
			visited.add(stateKey);

			const node = graph.nodes[nodeId];
			if (!node) continue;
			const requiresPlayer = PLAYER_REQUIRED_NODE_TYPES.has(node.type) || branchRequiresPlayer(node);

			if (
				requiresPlayer &&
				!hasPlayer &&
				!reported.has(nodeId)
			) {
				reported.add(nodeId);
				report.errors.push(
					issue(
						"error",
						"items",
						entityId,
						["graph", "nodes", nodeId],
						`${node.type} requires a player but is reachable from ${entryNode.type} without one — add an Interact, OnConsume, or OnProximity (OnEnter / OnExit) before this node, or the server will crash when the entity spawns`,
						"missing_field",
						{ nodeId, actionType: node.type },
					),
				);
				continue;
			}

			const wholeNodeBringsPlayer = PLAYER_BRINGING_NODE_TYPES.has(node.type);
			const handleSet = PLAYER_BRINGING_HANDLES[node.type];
			for (const edge of adjacency.get(nodeId) ?? []) {
				const edgeBringsPlayer =
					wholeNodeBringsPlayer || (handleSet?.has(edge.handle) ?? false);
				stack.push([edge.target, hasPlayer || edgeBringsPlayer]);
			}
		}
	}
};
